// EnhancementSlice.ts

import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  EnhancementState,
  EnhancementResponse,
  EnhancementField,
} from "../interfaces/IEnhancement";
import { uploadDocs } from "../apis/caseService";
import { fetchProcedures, fetchProceduresNew, fetchSpecialities, fetchSpecialitiesNew } from "../apis/patientService";

const initialState: EnhancementState = {
  activeStep: 0,
  enhancementData: null,
  isLoading: false,
  selectedFiles: [],
  uploadedDocumentUrl: [],
  error: null,
  editMode: false,
  editedFields: {},
  isSaving: false,
  procedures: [],
  specialities: []
};

export const UploadDocument = createAsyncThunk(
  "enhancement/UploadDocument",
  async ({ formData, accessToken }: { formData: any; accessToken: string }) => {
    const response = await uploadDocs(formData, accessToken);
    return response;
  }
)

export const fetchProceduresForEnhancement = createAsyncThunk(
  "enhancement/fetchProcedures",
  async (data: any) => {
    const response = await fetchProcedures(data);
    return response;
  }
);

export const fetchSpecialitiesForEnhancement = createAsyncThunk(
  "enhancement/fetchSpecialities",
  async () => {
    const response = await fetchSpecialities();
    return response;
  }
);

export const fetchProceduresForEnhancementNew = createAsyncThunk(
  "enhancement/fetchProceduresForEnhancementNew",
  async (data: any) => {
    const response = await fetchProceduresNew(data);
    return response;
  }
);

export const fetchSpecialitiesForEnhancementNew = createAsyncThunk(
  "enhancement/fetchSpecialitiesForEnhancementNew",
  async () => {
    const response = await fetchSpecialitiesNew();
    return response;
  }
);

const enhancementSlice = createSlice({
  name: "enhancement",
  initialState,
  reducers: {
    setEnhancementDataStart(state) {
      state.isLoading = true;
      state.error = null;
    },
    setEnhancementDataSuccess(
      state,
      action: PayloadAction<EnhancementResponse>
    ) {
      state.enhancementData = action.payload;
      state.isLoading = false;
    },
    setEnhancementDataFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },
    setSelectedFiles: (
      state,
      action: PayloadAction<{ file: File; documentType: string }[]>
    ) => {
      state.selectedFiles = action.payload;
    },
    // addUploadedDocumentUrl: (state, action: PayloadAction<string>) => {
    //   state.uploadedDocumentUrls.push(action.payload);
    // },
    // clearUploadedDocumentUrls: (state) => {
    //   state.uploadedDocumentUrls = [];
    // },
    toggleEditMode(state) {
      state.editMode = !state.editMode;
      if (!state.editMode) {
        state.editedFields = {};
      }
    },
    updateField(
      state,
      action: PayloadAction<{
        sectionKey: string;
        fieldId: string | undefined;
        value: any;
      }>
    ) {
      const { sectionKey, fieldId, value } = action.payload;
      if (fieldId) {
        if (sectionKey === "all" && fieldId === "all") {
          state.editedFields = value;
        } else {
          if (!state.editedFields[sectionKey]) {
            state.editedFields[sectionKey] = {};
          }
          state.editedFields[sectionKey][fieldId] = value;
        }
      }
    },
    saveChanges(state) {
      if (state.enhancementData && state.editedFields) {
        const newEnhancementData = JSON.parse(JSON.stringify(state.enhancementData));
        Object.entries(state.editedFields)?.forEach(([sectionKey, fields]) => {
          const section = newEnhancementData[sectionKey as keyof EnhancementResponse];
          if (Array.isArray(section)) {
            section?.forEach((sectionData) => {
              Object.entries(fields)?.forEach(([fieldId, value]) => {
                const fieldIndex = sectionData[Object.keys(sectionData)[0]].findIndex(
                  (f: EnhancementField) => f.id === fieldId
                );
                if (fieldIndex !== -1) {
                  sectionData[Object.keys(sectionData)[0]][fieldIndex] = {
                    ...sectionData[Object.keys(sectionData)[0]][fieldIndex],
                    data: value
                  };
                }
              });
            });
          }
        });
        
        // Add the uploaded document URLs to the enhancementData
        if (state.uploadedDocumentUrl.length > 0) {
          newEnhancementData.documentUrls = state.uploadedDocumentUrl;
        }
        
        state.enhancementData = newEnhancementData;
      }
      state.editMode = false;
      state.editedFields = {};
      // We're not clearing uploadedDocumentUrls here anymore
    },
    startSaving(state) {
      state.isSaving = true;
    },
    finishSaving(state) {
      state.isSaving = false;
    },
    clearUploadedDocumentUrlsAfterSave: (state) => {
      state.uploadedDocumentUrl = [];
    },
    setUploadedDocumentUrl(state, action: PayloadAction<Array<any>>) {
    
      state.uploadedDocumentUrl = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
    .addCase(UploadDocument.pending, (state) => {
      state.isLoading = true;
    })
    .addCase(
      UploadDocument.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.isLoading = false;
      }
    )
    .addCase(UploadDocument.rejected, (state) => {
      state.isLoading = false;
    })
    .addCase(fetchProceduresForEnhancement.pending, (state) => {
      state.isLoading = true;
    })
    .addCase(
      fetchProceduresForEnhancement.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.procedures = action.payload;
      }
    )
    .addCase(fetchProceduresForEnhancement.rejected, (state) => {
      state.isLoading = false;
    })
    .addCase(fetchSpecialitiesForEnhancement.pending, (state) => {
      state.isLoading = true;
    })
    .addCase(
      fetchSpecialitiesForEnhancement.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.specialities = action.payload;
      }
    )
    .addCase(fetchSpecialitiesForEnhancement.rejected, (state) => {
      state.isLoading = false;
    })

    .addCase(fetchProceduresForEnhancementNew.pending, (state) => {
      state.isLoading = true;
    })
    .addCase(
      fetchProceduresForEnhancementNew.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.procedures = action.payload;
      }
    )
    .addCase(fetchProceduresForEnhancementNew.rejected, (state) => {
      state.isLoading = false;
    })
    .addCase(fetchSpecialitiesForEnhancementNew.pending, (state) => {
      state.isLoading = true;
    })
    .addCase(
      fetchSpecialitiesForEnhancementNew.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.specialities = action.payload;
      }
    )
    .addCase(fetchSpecialitiesForEnhancementNew.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  setEnhancementDataStart,
  setEnhancementDataSuccess,
  setEnhancementDataFailure,
  setSelectedFiles,
  // addUploadedDocumentUrl,
  // clearUploadedDocumentUrls,
  toggleEditMode,
  updateField,
  saveChanges,
  startSaving,
  finishSaving,
  clearUploadedDocumentUrlsAfterSave,
  setUploadedDocumentUrl
} = enhancementSlice.actions;

export default enhancementSlice.reducer;